/**
 * useCrossTabAuthSync
 *
 * Listens for logout events broadcast from other tabs via localStorage.
 * The 'storage' event only fires in OTHER tabs when localStorage changes,
 * so there is no feedback loop from the originating tab's own logout.
 *
 * Extracted from SecureAuthProvider for clarity and testability.
 */
import { useEffect } from 'react';
import { authLogger } from '@/core/utils/logger';

interface UseCrossTabAuthSyncOptions {
  /** Called when a logout is detected in another browser tab */
  onLogoutDetected: () => void;
  /** Whether to listen for cross-tab logout events (default: true) */
  enabled?: boolean;
}

export function useCrossTabAuthSync({
  onLogoutDetected,
  enabled = true,
}: UseCrossTabAuthSyncOptions): void {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!enabled) return;

    const handleStorageEvent = (e: StorageEvent) => {
      if (e.key === 'auth_logout' && e.newValue) {
        authLogger.info(
          '[useCrossTabAuthSync] Logout detected in another tab, clearing local state'
        );
        onLogoutDetected();
      }
    };

    window.addEventListener('storage', handleStorageEvent);
    return () => {
      window.removeEventListener('storage', handleStorageEvent);
    };
  }, [onLogoutDetected, enabled]);
}
